<template>
    <data-table
        export-key="callHistories"
        settings-key="callHistories"
        @getList="getList"
        :headers="headers"
        :items="items"
        :loading="loading"
        :totalItems="totalItems"
        :filters="filters"
        :getAllList="getAllList"
    >
        <template #item.answer_status="{value}">
            <div class="rounded text-white text-center d-inline-block user-select-none pa-1 text-small" :class="value.includes('نشده') ? 'bg-error' : 'bg-success'">{{ value }}</div>
        </template>
    </data-table>
</template>
<script setup>
import { useApi } from '@/plugins/api';
import { useAuthStore } from '@/stores/auth';
import { useSystemStore } from '@/stores/system';
import { computed } from 'vue';

defineOptions({
    title: 'تاریخچه تماس ها',
    validate(context) {
        return context.auth.user.employee && context.system.systemSettings.voip_active;
    },
})
const api = useApi();
const system = useSystemStore();
const auth = useAuthStore();
const moment = useMoment();
const loading = ref(false);
const items = ref([]);
const totalItems = ref(0);
const headers = ref([
    {
        title: 'شماره تماس گیرنده',
        key: 'caller_phone',
        value: (item) => item.caller_phone,
        minWidth: 150,
    },
    {
        title: 'نام تماس گیرنده',
        key: 'caller',
        value: (item) => item.caller ? item.caller.name : undefined,
        minWidth: 180,
    },
    {
        title: 'شماره مخاطب',
        key: 'interphone',
        value: (item) => item.interphone,
        minWidth: 150,
    },
    {
        title: 'نام مخاطب',
        key: 'recipient',
        value: (item) => item.recipient ? item.recipient.name : undefined,
        minWidth: 180,
    },
    {
        title: 'سازمان مشتری',
        key: 'customer_organization',
        value: (item) => item.recipient && !item.recipient.employee ? item.recipient.customer_information.organization.name : (item.caller && !item.caller.employee ? item.caller.customer_information.organization.name : undefined),
        minWidth: 200,
    },
    {
        title: 'وضعیت',
        key: 'answer_status',
        value: (item) => item.answered_at ? 'پاسخ داده شده' : 'پاسخ داده نشده',
        minWidth: 100,
    },
    {
        title: 'مدت مکالمه',
        key: 'conversation_duration',
        value: (item) => {
            if (item.answered_at && item.finished_at) {
                let duration = Math.floor(((new Date(item.finished_at)).getTime() - (new Date(item.answered_at)).getTime()) / 1000);
                return `${Math.floor(duration / 60)}:${duration % 60}`
            }
            return undefined;
        },
        minWidth: 100,
    },
    {
        title: 'مدت انتظار',
        key: 'conversation_duration',
        value: (item) => {
            if (item.answered_at) {
                let duration = Math.floor(((new Date(item.answered_at)).getTime() - (new Date(item.started_at)).getTime()) / 1000);
                return `${Math.floor(duration / 60)}:${duration % 60}`
            }
            return undefined;
        },
        minWidth: 100,
    },
    {
        title: 'تاریخ و ساعت',
        key: 'started_at',
        cellProps: {style: 'direction: ltr; text-align: right'},
        value: (item) => item.started_at ? moment(item.started_at).format('jYYYY/jMM/jDD | HH:mm') : undefined,
        minWidth: 150,
    },
]);
const filters = ref({
    page: { type: 'hidden', label: 'صفحه', value: 1 },
    perPage: { type: 'hidden', label: 'تعداد آیتم در صفحه', value: 10 },
    general_based: { type: 'divider', label: 'بر اساس اطلاعات کلی تماس', value: null },
    status: { type: 'autocomplete', label: 'وضعیت', items: [{name: 'پاسخ داده شده', id: '1'}, {name: 'پاسخ داده نشده', id: '0'}], value: null },
    caller_phone: { type: 'text', label: 'شماره تماس گیرنده', value: null },
    interphone: { type: 'text', label: 'شماره مخاطب', value: null },
    date_based: { type: 'divider', label: 'بر اساس تاریخ تماس', value: null },
    from_date: { type: 'date', label: 'تاریخ', dateType: 'datetime', group: 'start', onChange: (value) => { filters.value.to_date.value = value > filters.value.to_date.value ? null : filters.value.to_date.value; filters.value.to_date.min = value; }, value: null },
    to_date: { type: 'date', label: 'تاریخ', dateType: 'datetime', group: 'end', min: null, value: null },
    user_based: { type: 'divider', label: 'بر اساس کاربر', value: null },
    user_type: { type: 'radio', label: 'نوع کاربر', items: [{id: null, name: 'مشتری'}, {id: '1', name: 'کاربر سازمانی'}], onChange: () => filters.value.users.value = [], value: null, permission: auth.user.full_admin },
    users: { type: false, label: 'کاربران', multiple: true, items: [{ id: auth.user.id, name: auth.user.name }, ...auth.user.to_departments.filter(x => x.users).map(x => x.users).flat().filter((value, index, self) => self.findIndex(x=> x.id == value.id) >= index)], value: [] },
    organizations: { type: 'autocomplete', label: 'سازمان مشتری', multiple: true, items: system.organizations, value: null, permission: false }
});
filters.value.users.type = computed(() => filters.value.user_type.value == null ? 'customerSearch' : auth.user.full_admin && filters.value.user_type.value === '1' ? 'autocomplete' : false);
filters.value.organizations.permission = computed(() => filters.value.user_type.value == null);
async function getList(finalFilters, cb = false) {
    loading.value = true;
    await api.getCallHistory(finalFilters).then(async res => {
        if (res && res.status == 'OK') {
            items.value = res.data;
            totalItems.value = res.total;
        }
    }).finally(() => loading.value = false);
    if (cb)
        cb();
}
async function getAllList(finalFilters) {
    let page = 1, data = [];
    while (page) {
        await api.getCallHistory({
            ...finalFilters,
            perPage: 200,
            page: page,
        }).then(async res => {
            if (res && res.status == 'OK') {
                data = data.concat(res.data);
                page = page == res.totalPages ? false : page + 1
            } else
                page = false;
        }).catch(() => page = false);
    }
    return data;
}
</script>
